import React from 'react'
import merge from 'deepmerge'
import getDisplayName from 'react-display-name'
import hoistNonReactStatics from 'hoist-non-react-statics'

import * as settings from './styles/settings'
import * as colors from './styles/colors'
import * as typography from './styles/typography'

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T[P] extends ReadonlyArray<infer U>
    ? ReadonlyArray<DeepPartial<U>>
    : DeepPartial<T[P]>
}

const defaultTheme = {
  settings,
  colors,
  typography,
}

export type UiTheme = typeof defaultTheme

const ThemeContext = React.createContext(defaultTheme)

function mergeOuterLocalTheme(outer: UiTheme, local: DeepPartial<UiTheme>): UiTheme {
  return merge(outer, local, {
    arrayMerge: (_destinationArray, sourceArray) => sourceArray,
  }) as UiTheme
}

export const createTheme: (theme: DeepPartial<UiTheme>) => DeepPartial<UiTheme> = theme => theme

export const useTheme = () => React.useContext(ThemeContext)

export interface IWithThemeProps {
  theme: UiTheme
}

export const withTheme = <TProps extends IWithThemeProps>(WrappedComponent: React.ComponentType<TProps>) => {
  const WithTheme: React.FC<Omit<TProps, 'theme'>> = props => {
    const theme = useTheme()

    return <WrappedComponent {...(props as any)} theme={theme} />
  }

  WithTheme.displayName = `withTheme(${getDisplayName(WrappedComponent)})`

  return hoistNonReactStatics(WithTheme, WrappedComponent)
}

export const ThemeProvider: React.FC<{
  theme: DeepPartial<UiTheme> | ((theme: UiTheme) => DeepPartial<UiTheme>)
}> = props => {
  const outerTheme = useTheme()

  const theme = React.useMemo(
    // eslint-disable-next-line react/prop-types
    () => mergeOuterLocalTheme(outerTheme, typeof props.theme === 'function' ? props.theme(outerTheme) : props.theme),
    [outerTheme, props]
  )

  return (
    // eslint-disable-next-line react/prop-types
    <ThemeContext.Provider value={theme}>{props.children}</ThemeContext.Provider>
  )
}

export const Theme: React.FC<{ children: (theme: UiTheme) => React.ReactElement | null }> = props =>
  props.children(useTheme())
